<template>
  <b-table
    :items="tableData"
    :fields="tableFields"
    :striped="true"
    responsive="sm"
    thead-class="tb-bg-gray"
    :class="['setting-table manage-org-table']"
    :tbody-tr-class="rowClass"
    show-empty
  >
    <template #empty="">
      <div
        class="tw-w-full tw-h-[321px] tw-text-[#878787] tw-text-[14px] tw-text-center"
      >
        <span>
          {{ getUiText("app-manage-orgs-empty-organisation-text-1") }}
        </span>
      </div>
    </template>
    <!-- Header orgName information -->
    <template #head(name)="data">
      <div class="tw-flex tw-items-center">
        <button class="icon-select" @click="toggleSelectAll(!isSelectAll)">
          <img
            v-show="isSelectAll"
            src="/img/elipse_selected_icon.svg"
            alt="eclipse_icon"
          />
          <img
            v-show="!isSelectAll"
            src="/img/elipse_icon.svg"
            alt="eclipse_icon"
          />
        </button>
        <span class="name-header-text">{{ data.label.toUpperCase() }}</span>
        <img
          src="/img/double-icon.svg"
          alt="double-icon"
          class="name-header-text tw-ml-1"
        />
      </div>
    </template>

    <!-- Cell name Data -->
    <template #cell(name)="data">
      <div class="tw-flex cell-org-name">
        <button class="icon-select" @click="toggleDetails(data.index)">
          <img
            v-show="selected[data.index]?.isSelectedRow"
            src="/img/elipse_selected_icon.svg"
            alt="eclipse_icon"
          />
          <img
            v-show="!selected[data.index]?.isSelectedRow"
            src="/img/elipse_icon.svg"
            alt="eclipse_icon"
          />
        </button>
        <div class="information-container tw-flex tw-items-center">
          <img
            v-if="data.value?.icon"
            :src="data.value.icon"
            alt="person_random"
          />
          <div class="information tw-flex tw-mr-12 tw-items-center tw-h-[20px]">
            <span class="name tw-font-semibold tw-pt-[2px]">{{
              data.value
            }}</span>
            <img
              v-if="data.item.isHome"
              class="tw-ml-[10px]"
              src="/img/home_icon.svg"
            />
            <span
              v-if="
                data.item.id === currentOrg.id && data.item.isDefaultVerified
              "
              class="current-text basic-text tw-items-center tw-mt-[2px] tw-h-[20px]"
            >
              {{ getUiText("app-manage-orgs-current-org-1") }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <!-- Cell Roles -->
    <template #cell(userRoles)="data">
      <div class="tw-flex">
        <div
          v-for="(role, index) in data.value"
          :key="index"
          class="cell-role tw-text-sm tw-font-medium tw-flex tw-items-center"
        >
          <span>{{ role.roleName }}</span>
        </div>
      </div>
    </template>

    <!-- Cell Status -->
    <template #cell(status)="data">
      <div class="tw-flex">
        <StatusBadge
          v-if="data.item.isDefaultVerified"
          class="tw-w-fit"
          :value="statusCode.verify"
        />
        <StatusBadge
          v-else-if="data.item.isArchived"
          class="tw-w-fit"
          :value="statusCode.archived"
        />
        <StatusBadge
          v-else-if="data.item.isVerified"
          class="tw-w-fit"
          :value="statusCode.pendingVerify"
        />
        <StatusBadge v-else class="tw-w-fit" :value="statusCode.notVerify" />
      </div>
    </template>

    <!-- Cell Action manage -->
    <template #cell(actionManage)="data">
      <ButtonShowMore
        v-if="!(data.item.id === currentOrg.id && data.item.isDefaultVerified)"
        :id-popover="'popover-btn-show-more-manage-org-table-' + data.index"
      >
        <!-- default -->
        <template v-if="data.item.isDefaultVerified" #default>
          <CustomDropDownItem
            :style="'normal'"
            :id-text="'app-manage-orgs-switch-org-text-1'"
            @click="() => $emit('switch_org', data.item)"
          />
          <CustomDropDownItem
            v-if="!data.item.isHome"
            :style="'danger'"
            :id-text="'app-manage-orgs-leave-org-text-1'"
            @click="() => $emit('leave_org', data.item)"
          />
        </template>

        <!-- isArchived -->
        <template v-else-if="data.item.isArchived" #default>
          <CustomDropDownItem
            :style="'normal'"
            :id-text="'app-manage-orgs-unarchive-request-org-text-1'"
            @click="() => $emit('archive_org', data.item, false)"
          />
          <CustomDropDownItem
            :style="'danger'"
            :id-text="'app-manage-orgs-delete-request-org-text-1'"
            @click="() => $emit('delete_org', data.item.id)"
          />
        </template>

        <!-- isVerified -->
        <template v-else-if="data.item.isVerified" #default>
          <CustomDropDownItem
            :style="'normal'"
            :id-text="'app-manage-orgs-create-request-org-text-1'"
            @click="() => $emit('create_org', data.item)"
          />
          <CustomDropDownItem
            :style="'danger'"
            :id-text="'app-manage-orgs-archive-request-org-text-1'"
            @click="() => $emit('archive_org', data.item, true)"
          />
        </template>

        <!-- Not archived -->
        <template v-else #default>
          <CustomDropDownItem
            :style="'normal'"
            :id-text="'app-manage-orgs-edit-request-org-text-1'"
            @click="() => $emit('edit_request', data.item)"
          />
          <CustomDropDownItem
            :style="'danger'"
            :id-text="'app-manage-orgs-archive-request-org-text-1'"
            @click="() => $emit('archive_org', data.item, true)"
          />
        </template>
      </ButtonShowMore>
    </template>
  </b-table>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import ButtonShowMore from "@/components/common/setting/buttonShowMore.vue";
import CustomDropDownItem from "@/components/common/customDropDownItem.vue";
import store from "@/store";
import utilities from "@/utils/utilities";
import StatusBadge from "@/components/common/setting/statusBadge.vue";
import { statusCode } from "@/utils/constants";

@Component({
  emits: [
    "switch_org",
    "leave_org",
    "select:one_row",
    "select:all_row",
    "create_org",
    "edit_request",
    "delete_org",
    "archive_org",
  ],
  components: { StatusBadge, ButtonShowMore, CustomDropDownItem },
})
export default class ManageOrgTable extends Vue {
  @Prop({ required: true }) readonly tableData: any[] = [];
  @Prop({ required: true }) readonly tableFields: any[] = [];
  @Prop({ required: true }) readonly currentOrg: any = {};
  public selected: any[] = [];
  public isSelectAll = false;
  public statusCode = statusCode;
  public status = {
    ACTIVE: {
      label: this.getUiText("app-component-table-cell-status-active-1"),
      className: "active",
    },
    INVITED: {
      label: this.getUiText("app-component-table-cell-status-invited-1"),
      className: "invited",
    },
    DEACTIVATED: {
      label: this.getUiText("app-component-table-cell-status-deactivated-1"),
      className: "deactivated",
    },
  };

  mounted() {
    if (this.tableData) this.handleStyleDeactivated();
  }

  created() {
    this.selected = this.tableData;
  }

  updated() {
    this.selected = this.tableData;
  }

  getUiText(id: string, isArray = false) {
    return utilities.getUiText(
      id,
      this.languageSelectedText,
      this.defaultUiText,
      isArray,
    );
  }

  get languageSelected() {
    return store.state.LanguagePicker.languageSelected;
  }
  get defaultUiText() {
    return store.state.LanguagePicker.defaultUiText;
  }
  get languageSelectedText() {
    return store.state.LanguagePicker.languageSelectedText;
  }

  handleStyleDeactivated() {
    this.tableData.forEach((item, index) => {
      if (item.status === "DEACTIVATED")
        this.tableData[index]._rowVariant = this.status.DEACTIVATED.className;
    });
  }

  toggleDetails(index) {
    this.$emit("select:one_row", [this.tableData[index]]);
    this.selected[index].isSelectedRow = !this.selected[index].isSelectedRow;

    if (this.selected[index].isSelectedRow) {
      this.tableData[index]._rowVariant = "item-selected";
    } else {
      this.tableData[index]._rowVariant = "tw-bg-transparent";
      this.isSelectAll = false;
    }
  }

  toggleSelectAll(isSelectAll) {
    this.isSelectAll = isSelectAll;
    this.$emit("select:all_row", this.isSelectAll ? this.tableData : []);
    this.selected.forEach((item) => {
      item.isSelectedRow = this.isSelectAll;
    });

    this.tableData.forEach((item) => {
      if (this.isSelectAll) {
        item._rowVariant = "item-selected";
      } else {
        item._rowVariant = "tw-bg-transparent";
      }
    });
  }

  rowClass(item, type) {
    if (!item || type !== "row") return;
    if (item.age > 30) return "table-success";
  }
}
</script>

<style lang="scss" scoped>
.b-table-selection-column {
  display: none !important;
}

.selected {
  background-color: transparent !important;
}

.table-primary {
  background-color: transparent !important;
}

.setting-table {
  font-size: 13px;
  line-height: 30px;
  width: 100%;
  display: block;
  overflow-x: auto;
  background: var(--UI-white, #ffffff);
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  border: 1px solid #eff0f1 !important;
  @media (max-width: 480px) {
    .table {
      table-layout: auto;
    }
  }

  thead {
    font-size: 10px;
    font-weight: 600;
    color: #878787 !important;
    background-color: #fcfcfd;
    text-transform: uppercase;

    tr {
      height: 56px;

      th {
        font-weight: 600;
        color: #878787 !important;
        border-top: 1px solid #eff0f1;
        border-bottom: 1px solid #eff0f1;
        padding: 17px 25px;
        white-space: nowrap;
        line-height: 100%;
        vertical-align: middle;
      }
    }
  }

  tbody {
    tr {
      height: 56px;

      td {
        padding: 18px 25px;
        border-top: 1px solid #eff0f1;
        vertical-align: middle;
        white-space: nowrap;
      }

      .td-actions {
        padding: 0;
      }
    }

    .cell-name {
      .name {
        img {
          width: 32px;
          height: 32px;
          margin-right: 15px;
          border-radius: 50%;
          border: 1px solid #eff0f1;
        }

        .information {
          span {
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .cell-org-name {
      .information-container {
        width: 100%;
        .information {
          width: 100%;

          .name {
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .cell-role {
      flex-direction: row;
      height: 34px;
      margin-right: 5px;
      border: 1px solid #eff0f1;
      border-radius: 100px;
      font-size: 11px;
      line-height: 14.3px;
      background-color: #ffffff;
      padding: 10px 13px;

      span {
        font-size: 11px;
        font-weight: 500;
        line-height: 110%;
      }

      svg {
        margin-left: 5px;
        fill: #231f20;
      }
    }

    .cell-status {
      padding: 3px 5px;
      height: 20px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 500;
    }

    .cell-last-active {
      color: #878787;
    }
  }

  .table-striped tbody tr:nth-of-type(odd) {
    background-color: #ffffff;
  }

  .name-header-text {
    color: #211f58;
    text-transform: uppercase;
    font-family: Poppins;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 100%; /* 10px */
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .table-striped tbody tr:nth-of-type(even) {
    background-color: #fcfcfd;
  }

  button {
    border: none;
    background-color: transparent;
  }

  .information {
    .name {
      font-size: 13px;
      font-weight: 500;
      line-height: 18px;
      color: #231f20;
    }
  }

  .grey-color {
    color: #878787;
  }

  .active {
    background-color: #d9ede2;
    color: #01893d;
  }

  .invited {
    background-color: #e4f1ff;
    color: #0345bd;
  }

  .deactivated {
    background: var(
      --ui-background,
      linear-gradient(45deg, #f1f2f3 0%, #eeeff0 100%)
    );
    color: #b7b7b7;
  }

  .table-deactivated {
    color: #b7b7b7 !important;

    .name,
    .email {
      color: #b7b7b7 !important;
    }

    .cell-role {
      svg {
        fill: #b7b7b7 !important;
      }
    }
  }

  .icon-select {
    margin-right: 20px;
  }

  .table-item-selected {
    background-color: #f2f8ff !important;
  }

  .current-text {
    color: #387dff;
    text-transform: uppercase;
    border: 1px solid #eff0f1;
    border-radius: 2px;
    padding: 5px 6px;
    font-style: normal;
    font-weight: 700;
    font-size: 10px;
    line-height: 100%;
    letter-spacing: 1px;
    margin-left: 20px;
  }
}
</style>
